import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { ActivatedRoute } from "@angular/router";
import * as _ from 'lodash';
import moment from 'moment-timezone';
import { MqttService } from "ngx-mqtt";
import { Subject } from "rxjs/internal/Subject";
import { debounceTime, takeUntil } from "rxjs/operators";
import { EPSEvent } from "../../../classes/event";
import { EventNode, GroupedEventNodes } from "../../../classes/event_node";
import { LoaderActivator } from '../../../classes/loader_activator';
import { ModalDatePickerData } from "../../../classes/modalDatePickerData";
import { PaginationMeta } from "../../../classes/paginationMeta";
import { Product } from "../../../classes/product";
import { PaginatorComponent } from "../../../directives/paginator/paginator.component";
import { EventListService } from "../../../services/event-list.service";
import { EventManagementService } from "../../../services/event-management.service";
import { PaginatedNodeService } from "../../../services/paginated-node.service";
import { RefDataService } from "../../../services/ref-data.service";
import { ListTab, SharedService } from "../../../services/shared.service";
import { UserService } from "../../../services/user.service";
import { CustomModalWrapper } from "../../modals/enel-modal/enel-modal.component";
import { EventExtendModalComponent } from "../../modals/event-extend-modal/event-extend-modal.component";

@Component({
  standalone: false,
  selector: 'app-utility-portal-event-details',
  templateUrl: './utility-portal-event-details.component.pug',
  styleUrls: ['./utility-portal-event-details.component.scss'],
  providers: [PaginatedNodeService]
})
export class UtilityPortalEventDetailsComponent implements OnInit, OnDestroy {

  private ngUnsubscribe: Subject<any> = new Subject();

  @ViewChild(PaginatorComponent, { static: false }) paginator: PaginatorComponent;

  comProductObj:Product;
  eventObj: EPSEvent = null;
  performanceData;
  perfInterval: any;
  events: Array<EPSEvent> = null;
  workflowStatuses = null;
  eventsLoaded:boolean = false;
  eventsFiltered:boolean = false;
  totalObligation:number = null;
  currentPerformance:number = 0;
  overallPerformance:number = 0;
  changingEndTime:boolean = false;

  allNodes = [];
  groupedNodes: GroupedEventNodes = new GroupedEventNodes();
  paginationMeta: PaginationMeta = { total_items: 0, total_pages_count: 0, total_items_in_current_page: 0, page: 1, per_page: 50 } as PaginationMeta;
  fetchingNodes: boolean = false;

  listColumns = [];
  _selectedColumns = [];
  statuses = [];
  confirmationStatuses = [];
  registrationTypes = [];

  //MQTT
  currentEventSubs = [];
  eventSubscribed:boolean = false;

  showPerformance:boolean = true;
  showObligation:boolean = true;
  utilityConfig = {};
  params: {program: string, product: string, start_time: string, end_time: string, type: string};

  filterValues: { [key: string]: any } = {};
  private textFilterSubject = new Subject<{ field: string; value: string }>();

  private readonly serverParamMap = {
    'eventNodeStatusForSort': 'event_node_status',
    'site_name': 'site_name',
    'registration_type_display_label': 'registration_type',
    'confStatus': 'confirmation_status'
  };

  constructor(
      private sharedService: SharedService,
      private eventManagementService: EventManagementService,
      private userService: UserService,
      private epsMqttService: MqttService,
      private listService: EventListService,
      private route: ActivatedRoute,
      private PNS: PaginatedNodeService,
      private refDataService: RefDataService
  ) {
      // Set the groups and whatnot for the new event
      const {unfilteredEvents$, loadingEvents$, workflowStatuses$} = this.listService;
      unfilteredEvents$.pipe().subscribe(resp => this.filterEvents(resp));

      loadingEvents$.pipe().subscribe(resp => this.eventsLoaded = !resp);

      workflowStatuses$.pipe().subscribe(resp => this.workflowStatuses = resp);

      this.PNS.fetchingNodes$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(resp => this.fetchingNodes = resp);
      this.PNS.groupedNodes$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(resp => this.handleNewNodesList(resp));

      this.textFilterSubject.pipe(
        debounceTime(600),
        takeUntil(this.ngUnsubscribe)
      ).subscribe(({ field, value }) => {
        this.applyFilter(field, value || null);
      });
  }

  ngOnInit() {
    this.params = this.route.snapshot.queryParams as any;
    this.sharedService.setListTab((<any>ListTab['UTILITY_DETAILS']));

    this.statuses = [
      {label: 'Active', value: 'Active'},
      {label: 'Completed', value: 'Completed'},
    ];
    this.confirmationStatuses = [
      { label: 'Confirmed', value: 'Confirmed' },
      { label: 'Preauthorized', value: 'Preauthorized' },
      { label: 'Unconfirmed', value: 'Unconfirmed' },
    ];

    this.refDataService.getRegistrationTypes().pipe(takeUntil(this.ngUnsubscribe)).subscribe(types => {
      this.registrationTypes = types.map(t => ({ label: t.display_label, value: t.code }));
      this.initListColumns();
    });

    // Fetch the utility config, then trigger the "events" lookup, which calls filterEvents
    this.eventManagementService.get('/v1/utility_config', {}).pipe(takeUntil(this.ngUnsubscribe)
    ).subscribe(response => {
      this.utilityConfig = response.data;
      this.listService.getEventsFromDate(true, true, this.params['start_time'], this.params['program'], this.params['type']);
    }, error => {
      console.dir("Error getting utility config: ");
      console.dir(error);
    });
  }

  @Input() get selectedColumns(): any[] {
    return this._selectedColumns;
  }

  hasColumn(name) {
    var num = _.findIndex(this._selectedColumns, c => {
      return c.header === name;
    });

    return num !== -1;
  }

  initListColumns() {
    this.listColumns = [
      { field: 'eventNodeStatusForSort', header: 'Status', filter: true, options: this.statuses },
      { field: 'site_name', header: 'Site', filter: true },
      { field: 'registered_capacity_value', header: 'Expected Performance' },
      { field: 'last_current_performance_value', header: 'Last Performance Value' },
      { field: 'last_current_performance_percentage', header: 'Last Performance %' },
      { field: 'overall_performance_value', header: 'Overall Performance' },
      { field: 'average_performance_percentage', header: 'Average Performance %' },
      { field: 'shortfall', header: 'Shortfall' },
      { field: 'registration_type_display_label', header: 'Registration Type', filter: true, options: this.registrationTypes },
      { field: 'confStatus', header: 'Confirmation Status', filter: true, options: this.confirmationStatuses },
      { field: null, header: 'Info' },
    ];
    this._selectedColumns = this.listColumns;
  }

  set selectedColumns(val: any[]) {
    //restore original order
    this._selectedColumns = this.listColumns.filter(col => val.includes(col));
  }

  fixedDecimals(value, numDecimals = 3) {
    if(typeof(value) === 'number')
      return value.toFixed(numDecimals);
    else
      return null;
  }

  filterEvents(allEvents) {
    if(!this.params.program || !this.params.type || !this.params.start_time) {
      console.log("Incorrect URL parameters. Couldn't filter events.");
      this.eventsFiltered = true;
      return;
    }

    const programConfig = this.utilityConfig[this.params.program];
    this.showPerformance = (programConfig?.show_performance_column !== false);
    this.showObligation = (programConfig?.show_obligation !== false);

    this.events = allEvents.filter(ev => {
        return ev.program_id === this.params.program &&
               ev.product_id === this.params.product &&
               ev.event_start_dttm_utc === this.params.start_time &&
               //Older links don't carry the end time, in that case group on the start time alone
               (!this.params.end_time || String(ev.event_end_dttm_utc) === this.params.end_time) &&
               ev.status !== "Cancelled";
    });

    if(this.events.length) {
        this.eventObj = this.events[0];
        this.aggregateEventTotals();
    } else {
        this.eventObj = null;
        this.eventsFiltered = true;
        return;
    }

    this.sharedService.setProgressStatus(this.eventObj)

    this.eventsFiltered = true;

    // Get the product from COM now that we have the event. But only if we don't have it yet.
    if (!this.comProductObj) {
      this.eventManagementService.get('/v1/product/' + this.eventObj.product_id, {})
        .toPromise()
        .then((response) => {
            this.comProductObj = response.data;

            //Set defaults for target_type related attributes
            if (!this.comProductObj.target_type) {
              this.comProductObj.target_type = "DROP_BY";
            }
            if (!this.comProductObj.performance_target_min && this.comProductObj.performance_target_min !== 0) {
              this.comProductObj.performance_target_min = 100;
            }
            if (this.comProductObj.target_type === "RANGE" && !this.comProductObj.performance_target_max && this.comProductObj.performance_target_max !== 0) {
              this.comProductObj.performance_target_max = this.comProductObj.performance_target_min * 1.5;
            }

            this.subscribeToPerformance();
        })
        .catch((error) => {
            console.dir("Error getting product");
            console.dir(error);
        });
    } else {
      this.subscribeToPerformance();
    }
  }

  fetchEventNodes() {
    if (!this.eventSubscribed) {
      this.eventSubscribed = true;
      this.subscribeToAllEventData();
    }

    const model = { eventIds: this.events.map(e => e.event_id), ...this.filterValues };
    const currentPage = this.paginator?.currentPage || 1;
    const pageSize = this.paginator?.pageSize || 50;
    this.PNS.getNodes(model as any, currentPage, pageSize, false, false, true);
  }

  onFilterChange(field: string, event: any) {
    const value = event?.value ?? event;
    this.applyFilter(field, value?.length ? value : null);
  }

  onTextFilterInput(field: string, event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.textFilterSubject.next({ field, value });
  }

  private applyFilter(field: string, value: any) {
    const serverParam = this.serverParamMap[field] || field;
    if (value === null || value === undefined) {
      delete this.filterValues[serverParam];
    } else {
      this.filterValues[serverParam] = value;
    }
    this.paginator?.reset();
    this.fetchEventNodes();
  }

  onPaginate() {
    this.fetchEventNodes();
  }

  handleNewNodesList(gn: GroupedEventNodes) {
    if (!gn) return;
    this.groupedNodes = gn;
    if (this.PNS.paginationMeta) this.paginationMeta = this.PNS.paginationMeta;
    this.setConfStatusForNodes(gn.activeNodes);
    this.setConfStatusForNodes(gn.completedNodes);
    this.aggregatePerformance();
  }

  aggregatePerformance() {
      this.groupedNodes.allNodes.forEach((node) => {
        node.shortfall = Math.max(node.shortfall, 0);
        node.overall_performance_value = this.sharedService.getOverallNodePerformance(node, this.comProductObj);
      });
      this.allNodes = this.groupedNodes.allNodes;
      this.overallPerformance = this.events.reduce((r, ev) => r + this.sharedService.getOverallPerformance(ev, this.comProductObj), 0);
      this.aggregateEventTotals();
  }

  aggregateEventTotals() {
      this.events.forEach(ev => this.sharedService.setProgressStatus(ev));
      this.totalObligation = this.sharedService.getGroupObligation(this.events);
      this.currentPerformance = this.events.reduce((r, ev) => r + (ev.last_current_performance_value || 0), 0);
  }

  subscribeToAllEventData() {
    const event_ids = this.events.map(e => e.event_id);

    // Unsubscribe to the previous one and subscribe to the new batch of events
    this.currentEventSubs.forEach(sub => sub.unsubscribe());

    // Clear it
    this.currentEventSubs = [];

    const onMessage = _.throttle(() => {
        try {
            this.fetchEventNodes();
        } catch(err) {
            console.log("Error updating event in UI: " + JSON.stringify(err));
        }
    }, 5000);

    this.sharedService.subscribeToTopic(
        null,
        event_ids,
        "eps_event",
        this.epsMqttService,
        msg => onMessage(),
        true,
        this.currentEventSubs,
        this.ngUnsubscribe,
        this.eventManagementService
    );
  }

  subscribeToPerformance() {
      if (this.perfInterval) return;

      const event_ids = this.events.map(e => e.event_id);

      // We'll get a ton of messages all at once. Call to refresh the
      // performance data at most once per 5 seconds.
      const fetchPerformance = _.throttle(() => {
          const perfParams = {event_ids, performance_aggregate_type: this.comProductObj?.performance_aggregate_type};
          return Promise.all([
              this.fetchEventNodes(),
              this.eventManagementService
                  .post('/v1/event_performance', perfParams, {})
                  .toPromise()
                  .then(response => this.performanceData = response.data)
                  .catch(err => {
                      console.dir("Error getting event performance data: ");
                      console.dir(err);
                  })
            ]);
      }, 5000);

      // Get performance and active event nodes, then subscribe to any changes
      this.perfInterval = setInterval(() => fetchPerformance(), 60 * 1000);
      fetchPerformance().then(() => {
          const allNodeIDs = this.allNodes.map(node => node.event_node_id.toString());
          this.sharedService.subscribeToTopic(
              null, // unused variable
              allNodeIDs,
              'NODE_PERFORMANCE',
              this.epsMqttService,
              msg => fetchPerformance(),
              false,
              [],
              this.ngUnsubscribe,
              this.eventManagementService
          );
      });
  }

  getEventType(code, type) {
    //Some programs have an override for this event type
    if (code === "VOLUNTARY") {
      if (this.utilityConfig[this.params['program']] && this.utilityConfig[this.params['program']].voluntary_event_type_override)
        return this.utilityConfig[this.params['program']].voluntary_event_type_override;
      else
        return "Official - Emergency";
    }
    return type;
  }

  getPerfDisplay(node: EventNode, type) {
    this.sharedService.setProgressStatus(this.eventObj);
    if (node.estimate_performance_ind) {
      return ' - ';
    } else {
      const status = this.eventObj.event_progress_status;
      switch (type) {
        case 'expectedValue':
          return this.sharedService.convertProductUOM(node.registered_capacity_value, this.comProductObj, true);
        case 'lastValue':
          if(status === 'BEFORE') {
            return '';
          } else if( status === 'DURING') {
            return this.sharedService.convertProductUOM(node.last_current_performance_value, this.comProductObj, true);
          } else {
            return ' - ';
          }
        case 'lastPercent':
          if(status === 'BEFORE') {
            return '';
          } else if( status === 'DURING') {
            return this.fixedDecimals(node.last_current_performance_percentage, 1);
          } else {
            return ' - ';
          }
        case 'overAllValue':
          return status !== 'BEFORE' ? this.sharedService.convertProductUOM(node.overall_performance_value, this.comProductObj, true) : '';
        case 'averagePercent':
          return status !== 'BEFORE' ? this.fixedDecimals(node.average_performance_percentage, 1) : '';
        case 'shortfall':
          return status === 'BEFORE' ? '' : this.sharedService.convertProductUOM(node.shortfall, this.comProductObj, true);
        default:
          return '';
      }
    }
  }


  eventEndInPast(): boolean {
    return this.eventObj.event_progress_status === 'AFTER';
  }

  hasUtilityDispatchRole(): boolean {
    return this.userService.hasUtilityDispatchRole();
  }

  showMoreInfo(eventNode): void {
    const url = window.location.origin + '/utility/eventnode/' + eventNode.event_node_id;
    window.open(url, '_blank');
  }

  private setConfStatusForNodes(nodes: EventNode[] = []) {
    if (!nodes) return;
    nodes.forEach((node) => {
      node.confStatus = node.confirmed ? 'Confirmed' : node.preauthorized ? 'Preauthorized' : 'Unconfirmed';
    });
  }

  endEvent(): void {
    const controller = this;

    let bulkEvents = [];

    this.events.forEach(event => {
      bulkEvents.push({event: event, obligation: null});
    });

    let start = this.eventObj.event_start_dttm_utc;
    let end = !!this.eventObj.event_end_dttm_utc ? this.eventObj.event_end_dttm_utc :  moment(start).add(this.comProductObj.max_event_duration, 'ms').toISOString();
    let timePickerDataEnd = new ModalDatePickerData(this.eventObj.event_end_dttm_utc, 'End', start, null, this.comProductObj.max_event_duration, false, false, false, false);

    this.sharedService.activateModal({
      headerText: "Update Event End Time",
      bodyText: "Updating " + (bulkEvents.length) + " events.",
      customContent: new CustomModalWrapper(EventExtendModalComponent, {
        program_time_zone: this.comProductObj.timezone,
        events: bulkEvents,
        isUtility: true,
        timePickerData: [timePickerDataEnd],
        buttonText: 'Update End Time',
        utility: true,

        confirmFunction: function (time, evData) {

          let eventIds = [];
          evData.forEach((e) => {
            eventIds.push(e.event.event_id);
          });

          let body =  {
            event_ids : eventIds
          };

          controller.sharedService.activateLoader(new LoaderActivator(new Promise(function (resolve, reject) {
            resolve(controller.eventManagementService.put('/v1/utility_event/event_end_time/' + time[0], body, {}))
          }), function () {
            controller.sharedService.popSuccess("Success! The end time has been updated. It may take up to 5 minutes to see it reflected on this page. If you don't see the new end time within 5 minutes please call Enel X at 617-692-2003.");
          }, function (error) {
            console.dir(error.error);
          }, false, "Updating End Times", false, false));
        }
      })
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(true);
    clearInterval(this.perfInterval);
  }
}
